import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Grid, GridItem, Button, Spinner, Center, Alert, AlertIcon } from '@chakra-ui/react';
import JobDetailsHeader from '../components/CareersComponents/JobDetails/JobDetailsHeader';
import JobDetailsMainInfo from '../components/CareersComponents/JobDetails/JobDetailsMainInfo';
import JobDetailsDescription from '../components/CareersComponents/JobDetails/JobDetailsDescription';
import JobDetailsListSection from '../components/CareersComponents/JobDetails/JobDetailsListSection';
import JobDetailsSidebar from '../components/CareersComponents/JobDetails/JobDetailsSidebar';
import CareersFooter from '../components/CareersComponents/CareersFooter';
import SimilarJobPosts from '../components/CareersComponents/JobDetails/SimilarJobPosts';
import JoinUsTeamActivity from '../components/CareersComponents/JoinUsComponents/JoinUsTeamActivity';
import ApplicationForm from '../components/CareersComponents/JobDetails/ApplicationForm';
import { getJobCircularById } from '../services/jobCircularsService';
import { getLocations } from '../services/locationsService';

const JobDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isApplicationFormOpen, setIsApplicationFormOpen] = useState(false);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { job: jobFromState, fromInternship } = location.state || {};

  // Fetch job data if not available from navigation state
  useEffect(() => {
    const fetchJobData = async () => {
      // If we have job data from navigation state, use it
      if (jobFromState) {
        setJob(jobFromState);
        return;
      }

      // If we don't have job data, fetch it using the ID from URL
      if (id) {
        try {
          setLoading(true);
          setError(null);
          
          // Fetch locations for proper mapping
          const locationsResult = await getLocations();
          const locations = locationsResult.success ? locationsResult.locations : [];
          
          // Fetch job data by ID
          const result = await getJobCircularById(id, locations);
          
          if (result.success && result.job) {
            setJob(result.job);
          } else {
            setError('Job not found');
          }
        } catch (err) {
          setError('Failed to load job details: ' + err.message);
        } finally {
          setLoading(false);
        }
      } else {
        setError('No job ID provided');
      }
    };

    fetchJobData();
  }, [id, jobFromState]);

  // Loading state
  if (loading) {
    return (
      <Box minH="100vh" bg="#F6FFF6">
        <JobDetailsHeader title="Job Details" subtitle="Secure the opportunity that matches your ambition" />
        <Center py={20}>
          <Spinner size="xl" color="#234F4E" />
        </Center>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box minH="100vh" bg="#F6FFF6">
        <JobDetailsHeader title="Job Details" subtitle="Secure the opportunity that matches your ambition" />
        <Box maxW="800px" mx="auto" px={{ base: 4, md: 6 }} py={8}>
          <Alert status="error" borderRadius="md" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
          <Button onClick={() => navigate('/job-opening')} colorScheme="blue">
            Back to Job Openings
          </Button>
        </Box>
      </Box>
    );
  }

  // No job data state
  if (!job) {
    return (
      <Box minH="100vh" bg="#F6FFF6">
        <JobDetailsHeader title="Job Details" subtitle="Secure the opportunity that matches your ambition" />
        <Box maxW="800px" mx="auto" px={{ base: 4, md: 6 }} py={8}>
          <Alert status="info" borderRadius="md" mb={4}>
            <AlertIcon />
            No job data found.
          </Alert>
          <Button onClick={() => navigate('/job-opening')} colorScheme="blue">
            Back to Job Openings
          </Button>
        </Box>
      </Box>
    );
  }
  // Convert API data to display format
  const jobDescription = job.job_context || 'Job description not available';
  const responsibilities = job.responsibility ? job.responsibility.replace(/<[^>]*>/g, '').split('.').filter(item => item.trim()) : [];
  const requirements = job.job_requirement ? job.job_requirement.replace(/<[^>]*>/g, '').split('.').filter(item => item.trim()) : [];
  const benefits = job.benefits ? job.benefits.replace(/<[^>]*>/g, '').split('.').filter(item => item.trim()) : [];
  
  const aboutJob = [
    { label: 'Deadline', value: job.deadline || 'Not specified' },
    { label: 'Job Posted On', value: new Date(job.created_at).toLocaleDateString() || 'Not specified' },
    { label: 'Salary', value: job.salary === 'Negotiable' || !job.salary ? 'Negotiable' : `${job.salary} BDT` },
    { label: 'Job Type', value: job.type || 'Not specified' },
    { label: 'Education', value: job.education_requirement ? job.education_requirement.replace(/<[^>]*>/g, '') : 'Not specified' },
    { label: 'Vacancy', value: job.job_vacancy || 'Not specified' },
    { label: 'Experience', value: job.experience || 'Not specified' },
    { label: 'Job Location', value: job.location || 'Not specified' },
    { label: 'Team', value: job.team_name || 'Not specified' },
  ];

  const categories = job.category_name ? [job.category_name] : [];
  const skills = []; // API doesn't provide skills data

  return (
    <Box minH="100vh" bg="#F6FFF6">
      <JobDetailsHeader title="Job Details" subtitle="Secure the opportunity that matches your ambition" />
      <Box maxW="1200px" mx="auto" px={{ base: 2, md: 6 }} py={{ base: 4, md: 8 }}>
        <Grid 
          templateColumns={{ base: '1fr', md: '2.5fr 1fr' }}
          gap={{ base: 6, md: 10 }}
          alignItems="flex-start"
        >
          {/* Main Info */}
          <GridItem colSpan={2}>
            <Box bg="white" borderRadius="xl" boxShadow="md" mb={4} p={{ base: 4, md: 8 }}>
              <JobDetailsMainInfo job={job} onApplyClick={() => setIsApplicationFormOpen(true)} />
            </Box>
          </GridItem>
          {/* Description/Details */}
          <GridItem colSpan={{ base: 2, md: 1 }}>
            <Box bg="white" borderRadius="xl" boxShadow="md" p={{ base: 4, md: 8 }}>
              <JobDetailsDescription title="Description" content={jobDescription} />
              {responsibilities.length > 0 && (
                <JobDetailsListSection title="Key Responsibilities" items={responsibilities} />
              )}
              {requirements.length > 0 && (
                <JobDetailsListSection title="Job Requirements" items={requirements} listProps={{ pl: 0 }} />
              )}
              {benefits.length > 0 && (
                <JobDetailsListSection title="Benefits" items={benefits} listProps={{ pl: 0 }} />
              )}
              <Button 
                bg="#234F4E" 
                color="white" 
                borderRadius="full" 
                fontWeight="bold" 
                px={10} 
                py={6} 
                fontSize="xl" 
                _hover={{ bg: '#1B3A4B' }} 
                mt={4} 
                alignSelf="flex-start"
                onClick={() => setIsApplicationFormOpen(true)}
              >
                Apply
              </Button>
                
            </Box>
          </GridItem>
          {/* Sidebar */}
          <GridItem colSpan={{ base: 2, md: 1 }}>
            <Box position={{ base: 'static', md: 'sticky' }} top={{ md: '32px' }} zIndex={5}>
              <JobDetailsSidebar about={aboutJob} categories={categories} skills={skills} />
            </Box>
          </GridItem>
        </Grid>
      </Box>
      <SimilarJobPosts currentJobId={job?.id} />
      {/* কেবল Internship পেজ থেকে এলে দেখাবো */}
      {fromInternship && <JoinUsTeamActivity />}
      <CareersFooter />
      
      {/* Application Form Modal */}
      <ApplicationForm 
        isOpen={isApplicationFormOpen}
        onClose={() => setIsApplicationFormOpen(false)}
        jobTitle={job?.job_title || 'Position'}
        jobCircularId={job?.id || null}
        jobInfo={job}
      />
    </Box>
  );
};

export default JobDetails; 